import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Building,
  Pencil,
  Phone,
  Plus,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  type EmergencyContactDto,
  useCreateSiteContact,
  useDeleteContact,
  useSiteEmergencyContacts,
  useUpdateContact,
} from '../api/useEmergencyContacts.js';
import { useRoster } from '../api/useRoster.js';
import {
  type SiteDto,
  useAddSiteMembers,
  useCreateSite,
  useDeleteSite,
  useRemoveSiteMember,
  useSite,
  useSiteMembers,
  useSites,
  useUpdateSite,
} from '../api/useSites.js';
import { EmptyState } from '../components/EmptyState.js';
import { RoleGate } from '../components/RoleGate.js';
import { formatRelative } from '../lib/format.js';

// Sites page. Two modes:
//   • /sites      — list + create
//   • /sites/:id  — detail, edit, members, emergency contacts
// Admin-only mutations; monitors get a read-only view.

export default function Sites() {
  return (
    <div className="mx-auto max-w-6xl">
      <SitesList />
    </div>
  );
}

function SitesList() {
  const sites = useSites();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Field</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Sites
          </h1>
          <p className="text-sm text-ink-faint">
            Physical locations or facilities. State offices, RAC facilities, ward groups — anywhere
            you want to attach workers and emergency contacts.
          </p>
        </div>
        <RoleGate allow={['admin']} fallback={null}>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple"
          >
            <Plus className="h-4 w-4" />
            New site
          </button>
        </RoleGate>
      </div>

      {sites.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load sites: {(sites.error as Error).message}
        </div>
      )}

      {sites.data && sites.data.sites.length === 0 && !showCreate && (
        <EmptyState
          icon={Building}
          title="No sites yet"
          description="Create one to start grouping workers by location or facility."
        />
      )}

      {sites.data && sites.data.sites.length > 0 && (
        <div className="panel">
          <ul className="divide-y divide-line">
            {sites.data.sites.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-3">
                <Building className="h-4 w-4 text-brand-purple" />
                <div className="flex-1">
                  <Link to={`/sites/${s.id}`} className="text-sm font-semibold hover:underline">
                    {s.name}
                  </Link>
                  <div className="text-xs text-ink-faint">
                    {s.address ?? <span className="italic">No address</span>}
                    <span className="mx-1">·</span>
                    {s.member_count} {s.member_count === 1 ? 'worker' : 'workers'}
                    <span className="mx-1">·</span>
                    {s.timezone}
                  </div>
                </div>
                <Link
                  to={`/sites/${s.id}`}
                  className="text-xs font-medium text-brand-purple hover:underline"
                >
                  Manage
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showCreate && <CreateSiteModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateSiteModal({ onClose }: { onClose: () => void }) {
  const create = useCreateSite();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [tz, setTz] = useState('Australia/Sydney');
  const [isOffice, setIsOffice] = useState(false);
  // Single text input for the coords pair (matches how Google Maps copies
  // them). Parsed on submit.
  const [coords, setCoords] = useState('');
  const parsedCoords = parseCoords(coords);
  const coordsInvalid = isOffice && coords.trim().length > 0 && !parsedCoords;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (coordsInvalid) return;
    await create.mutateAsync({
      name: name.trim(),
      address: address.trim() || null,
      timezone: tz,
      is_office: isOffice,
      latitude: isOffice ? (parsedCoords?.lat ?? null) : null,
      longitude: isOffice ? (parsedCoords?.lng ?? null) : null,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">New site</h2>
          <button type="button" onClick={onClose} className="text-ink-mute hover:text-ink-dim">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="VIC office, Maple RAC, etc."
              required
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Address (optional)</span>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Timezone</span>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="Australia/Sydney">Australia/Sydney (NSW, ACT, VIC, TAS)</option>
              <option value="Australia/Melbourne">Australia/Melbourne</option>
              <option value="Australia/Brisbane">Australia/Brisbane (QLD)</option>
              <option value="Australia/Adelaide">Australia/Adelaide (SA, NT)</option>
              <option value="Australia/Perth">Australia/Perth (WA)</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={isOffice}
              onChange={(e) => setIsOffice(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span className="text-xs text-ink-dim">
              <span className="font-medium text-ink">This site is an office</span>
              <span className="text-ink-faint">
                {' '}
                · used by the reimbursement report to credit office-bonus driving
              </span>
            </span>
          </label>
          {isOffice && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Office coordinates</span>
              <input
                type="text"
                value={coords}
                onChange={(e) => setCoords(e.target.value)}
                placeholder="-37.8136, 144.9631"
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
              <span className="text-[10px] text-ink-faint">
                Right-click the office on Google Maps → click the lat,lng to copy → paste here.
              </span>
              {coordsInvalid && (
                <span className="text-[11px] text-red-600">
                  Couldn't parse coordinates — expected "lat, lng" (e.g. -37.81, 144.96).
                </span>
              )}
            </label>
          )}
          {create.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {(create.error as Error).message}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-ink-faint hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending || !name.trim() || coordsInvalid}
              className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              {create.isPending ? 'Creating…' : 'Create site'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Parse a coords string. Accepts "lat, lng", "lat,lng", or "lat lng". Returns
// null if either component is out of range or non-numeric. Used by the office
// coords input on the Site forms.
function parseCoords(s: string): { lat: number; lng: number } | null {
  const cleaned = s.trim().replace(/[()]/g, '');
  if (!cleaned) return null;
  const parts = cleaned.split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const site = useSite(id);
  const members = useSiteMembers(id);
  const roster = useRoster();
  const [isEditing, setIsEditing] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);

  if (site.isLoading) {
    return <div className="mx-auto max-w-6xl text-sm text-ink-faint">Loading site…</div>;
  }
  if (site.isError || !site.data) {
    return (
      <div className="mx-auto max-w-6xl">
        <EmptyState
          icon={AlertTriangle}
          title="Site not found"
          description={(site.error as Error)?.message ?? 'This site may have been deleted.'}
        />
        <div className="mt-4">
          <Link to="/sites" className="text-sm text-brand-purple hover:underline">
            ← Back to sites
          </Link>
        </div>
      </div>
    );
  }

  const detail = site.data;
  const memberList = members.data?.members ?? [];
  const existingIds = new Set(memberList.map((m) => m.user_id));
  const assignable = (roster.data?.members ?? []).filter((m) => !existingIds.has(m.user_id));

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/sites"
        className="mb-4 inline-flex items-center gap-2 text-sm text-ink-faint hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sites
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-brand-purple" />
            <h1 className="font-heading text-2xl font-bold">{detail.name}</h1>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-ink-faint">
            {detail.address ? <span>{detail.address}</span> : <span>No address</span>}
            <span>·</span>
            <span>{detail.timezone}</span>
            <span>·</span>
            <span>
              {detail.member_count} {detail.member_count === 1 ? 'worker' : 'workers'}
            </span>
          </div>
        </div>
        <RoleGate allow={['admin']} fallback={null}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsEditing((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-line-strong px-2.5 py-1 text-xs hover:bg-surface-3"
            >
              <Pencil className="h-3 w-3" />
              {isEditing ? 'Done' : 'Edit'}
            </button>
            <DeleteSiteButton site={detail} onDeleted={() => navigate('/sites')} />
          </div>
        </RoleGate>
      </div>

      {isEditing && <EditSiteForm site={detail} onClose={() => setIsEditing(false)} />}

      {/* Members */}
      <section className="mt-6 panel">
        <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <h2 className="text-sm font-semibold">Workers assigned to this site</h2>
          <RoleGate allow={['admin']} fallback={null}>
            <button
              type="button"
              onClick={() => setShowAssign(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline"
            >
              <UserPlus className="h-3 w-3" /> Assign workers
            </button>
          </RoleGate>
        </header>
        {memberList.length === 0 ? (
          <div className="p-4 text-xs text-ink-faint">No workers assigned yet.</div>
        ) : (
          <ul className="divide-y divide-line">
            {memberList.map((m) => (
              <li key={m.user_id} className="flex items-center gap-3 px-4 py-2">
                <div className="flex-1 text-sm">
                  <div className="font-medium">{m.display_name}</div>
                  <div className="text-xs text-ink-faint">{m.email}</div>
                </div>
                <RoleGate allow={['admin']} fallback={null}>
                  <RemoveSiteMemberButton siteId={detail.id} userId={m.user_id} />
                </RoleGate>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Site-scoped emergency contacts */}
      <section className="mt-6 panel">
        <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <div>
            <h2 className="text-sm font-semibold">Emergency contacts for this site</h2>
            <p className="text-xs text-ink-faint">
              Notified when any worker assigned to this site triggers an alarm.
            </p>
          </div>
          <RoleGate allow={['admin']} fallback={null}>
            <button
              type="button"
              onClick={() => setShowAddContact(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline"
            >
              <Plus className="h-3 w-3" /> Add contact
            </button>
          </RoleGate>
        </header>
        <SiteContactsList siteId={detail.id} />
      </section>

      {showAssign && (
        <AssignSiteMembersModal
          siteId={detail.id}
          assignable={assignable}
          onClose={() => setShowAssign(false)}
        />
      )}
      {showAddContact && (
        <ScopedContactModal
          scope="site"
          parentId={detail.id}
          onClose={() => setShowAddContact(false)}
        />
      )}
    </div>
  );
}

function EditSiteForm({ site, onClose }: { site: SiteDto; onClose: () => void }) {
  const update = useUpdateSite();
  const [name, setName] = useState(site.name);
  const [address, setAddress] = useState(site.address ?? '');
  const [tz, setTz] = useState(site.timezone);
  const [isOffice, setIsOffice] = useState(site.is_office);
  const [coords, setCoords] = useState(
    site.latitude !== null && site.longitude !== null ? `${site.latitude}, ${site.longitude}` : '',
  );
  const parsedCoords = parseCoords(coords);
  const initialCoords =
    site.latitude !== null && site.longitude !== null ? `${site.latitude}, ${site.longitude}` : '';
  const coordsInvalid = isOffice && coords.trim().length > 0 && !parsedCoords;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (coordsInvalid) return;
    // Only send fields that actually changed. Coords reset to null when
    // is_office is toggled off so the geofence picks up the new state cleanly.
    const officeChanged = isOffice !== site.is_office;
    const coordsChanged = coords.trim() !== initialCoords.trim() || officeChanged;
    await update.mutateAsync({
      id: site.id,
      name: name !== site.name ? name : undefined,
      address: address !== (site.address ?? '') ? address || null : undefined,
      timezone: tz !== site.timezone ? tz : undefined,
      is_office: officeChanged ? isOffice : undefined,
      latitude: coordsChanged ? (isOffice ? (parsedCoords?.lat ?? null) : null) : undefined,
      longitude: coordsChanged ? (isOffice ? (parsedCoords?.lng ?? null) : null) : undefined,
    });
    onClose();
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 grid gap-3 rounded-lg bg-white p-4 shadow-panel">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-dim">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-line-strong px-2.5 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-dim">Timezone</span>
          <input
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="rounded-md border border-line-strong px-2.5 py-1 text-sm"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-ink-dim">Address</span>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="rounded-md border border-line-strong px-2.5 py-1 text-sm"
        />
      </label>
      <label className="inline-flex items-center gap-2 pt-1">
        <input
          type="checkbox"
          checked={isOffice}
          onChange={(e) => setIsOffice(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        <span className="text-xs text-ink-dim">
          <span className="font-medium text-ink">This site is an office</span>
          <span className="text-ink-faint">
            {' '}
            · credits office-bonus driving on the reimbursement report
          </span>
        </span>
      </label>
      {isOffice && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-dim">Office coordinates</span>
          <input
            value={coords}
            onChange={(e) => setCoords(e.target.value)}
            placeholder="-37.8136, 144.9631"
            className="rounded-md border border-line-strong px-2.5 py-1 text-sm"
          />
          <span className="text-[10px] text-ink-faint">
            Right-click the office on Google Maps → click the lat,lng to copy → paste here.
          </span>
          {coordsInvalid && (
            <span className="text-[11px] text-red-600">
              Couldn't parse coordinates — expected "lat, lng".
            </span>
          )}
        </label>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-xs text-ink-faint hover:bg-surface-3"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={update.isPending || coordsInvalid}
          className="rounded-md bg-brand-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </form>
  );
}

function DeleteSiteButton({ site, onDeleted }: { site: SiteDto; onDeleted: () => void }) {
  const del = useDeleteSite();
  async function onClick() {
    if (!confirm(`Delete "${site.name}"? This removes all worker assignments and contacts.`))
      return;
    await del.mutateAsync(site.id);
    onDeleted();
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1 text-xs text-red-700 hover:bg-red-50"
    >
      <Trash2 className="h-3 w-3" /> Delete
    </button>
  );
}

function RemoveSiteMemberButton({ siteId, userId }: { siteId: string; userId: string }) {
  const remove = useRemoveSiteMember();
  return (
    <button
      type="button"
      onClick={() => remove.mutate({ siteId, userId })}
      className="text-xs text-red-700 hover:underline"
      disabled={remove.isPending}
    >
      <UserMinus className="inline h-3 w-3" /> Remove
    </button>
  );
}

function AssignSiteMembersModal({
  siteId,
  assignable,
  onClose,
}: {
  siteId: string;
  assignable: { user_id: string; display_name: string; email: string }[];
  onClose: () => void;
}) {
  const add = useAddSiteMembers();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (picked.size === 0) return;
    await add.mutateAsync({ siteId, userIds: [...picked] });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Assign workers to this site</h2>
          <button type="button" onClick={onClose} className="text-ink-mute hover:text-ink-dim">
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="max-h-80 overflow-y-auto divide-y divide-line rounded-md border border-line">
          {assignable.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-ink-faint">
              No workers available to assign.
            </li>
          )}
          {assignable.map((m) => (
            <li key={m.user_id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={picked.has(m.user_id)}
                onChange={(e) => {
                  setPicked((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(m.user_id);
                    else next.delete(m.user_id);
                    return next;
                  });
                }}
                className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
              />
              <span className="flex-1">
                <span className="font-medium">{m.display_name}</span>
                <span className="ml-2 text-xs text-ink-faint">{m.email}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-ink-faint hover:bg-surface-3"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={add.isPending || picked.size === 0}
            className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
          >
            Assign {picked.size} worker{picked.size === 1 ? '' : 's'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SiteContactsList({ siteId }: { siteId: string }) {
  const contacts = useSiteEmergencyContacts(siteId);
  const list = contacts.data?.contacts ?? [];
  if (list.length === 0) {
    return (
      <div className="p-4 text-xs text-ink-faint">No contacts configured for this site yet.</div>
    );
  }
  return (
    <ul className="divide-y divide-line">
      {list.map((c) => (
        <ContactRowMini key={c.id} contact={c} />
      ))}
    </ul>
  );
}

export function ContactRowMini({ contact }: { contact: EmergencyContactDto }) {
  const _update = useUpdateContact();
  const del = useDeleteContact();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <li className="bg-brand-purple/5 p-3">
        <ScopedContactInline existing={contact} onClose={() => setEditing(false)} />
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-4 py-2">
      <Phone className="h-4 w-4 text-ink-mute" />
      <div className="flex-1 text-sm">
        <div className="font-medium">
          {contact.name}
          {contact.role && <span className="ml-2 text-xs text-ink-faint">· {contact.role}</span>}
        </div>
        <div className="text-xs text-ink-faint">
          {contact.phone_e164 && <span className="font-mono">{contact.phone_e164}</span>}
          {contact.email && <span className="ml-2 font-mono">{contact.email}</span>}
          {contact.notify_after_seconds > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
              <Bell className="h-2.5 w-2.5" />
              tier {Math.round(contact.notify_after_seconds / 60)} min
            </span>
          )}
        </div>
      </div>
      <RoleGate allow={['admin']} fallback={null}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-ink-faint hover:text-ink"
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirm(`Delete contact "${contact.name}"?`)) return;
              del.mutate(contact.id);
            }}
            className="text-xs text-red-700 hover:text-red-900"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </RoleGate>
    </li>
  );
}

// Inline edit form — used inside the contact row for quick edits.
function ScopedContactInline({
  existing,
  onClose,
}: {
  existing: EmergencyContactDto;
  onClose: () => void;
}) {
  const update = useUpdateContact();
  const [name, setName] = useState(existing.name);
  const [role, setRole] = useState(existing.role ?? '');
  const [phone, setPhone] = useState(existing.phone_e164 ?? '');
  const [email, setEmail] = useState(existing.email ?? '');
  const [notifyAfterMin, setNotifyAfterMin] = useState(
    Math.round(existing.notify_after_seconds / 60),
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      id: existing.id,
      name: name !== existing.name ? name : undefined,
      role: (role || null) !== existing.role ? role || null : undefined,
      phone_e164: (phone || null) !== existing.phone_e164 ? phone || null : undefined,
      email: (email || null) !== existing.email ? email || null : undefined,
      notify_after_seconds:
        notifyAfterMin * 60 !== existing.notify_after_seconds ? notifyAfterMin * 60 : undefined,
    });
    onClose();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="rounded border border-line-strong px-2 py-1 text-xs"
        />
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Role"
          className="rounded border border-line-strong px-2 py-1 text-xs"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+61…"
          className="rounded border border-line-strong px-2 py-1 text-xs font-mono"
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@…"
          className="rounded border border-line-strong px-2 py-1 text-xs font-mono"
        />
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-dim">
        Notify after
        <input
          type="number"
          min={0}
          value={notifyAfterMin}
          onChange={(e) => setNotifyAfterMin(Math.max(0, Number(e.target.value) || 0))}
          className="w-16 rounded border border-line-strong px-2 py-0.5 text-xs"
        />
        minutes (0 = immediate)
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="text-xs text-ink-faint hover:underline">
          Cancel
        </button>
        <button
          type="submit"
          disabled={update.isPending}
          className="rounded bg-brand-purple px-2 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </form>
  );
}

// Modal used by team / site / org pages to create a scope-targeted contact. The
// scope is determined by the caller — different scopes route to different POST
// endpoints under the hood.
export function ScopedContactModal({
  scope,
  parentId,
  onClose,
}: {
  scope: 'team' | 'site' | 'organisation';
  parentId?: string;
  onClose: () => void;
}) {
  const create = useCreateSiteContact();
  // We use the site mutation but the same body shape works for all three scopes;
  // future improvement: branch by scope. Simpler now to assume site.
  // (For the org and team flows, callers should swap to the right hook.)
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notifyAfterMin, setNotifyAfterMin] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || (!phone.trim() && !email.trim())) return;
    if (scope !== 'site' || !parentId) {
      alert(
        'This modal currently supports site scope only. Use the dedicated UI for other scopes.',
      );
      return;
    }
    await create.mutateAsync({
      site_id: parentId,
      name: name.trim(),
      role: role.trim() || null,
      phone_e164: phone.trim() || null,
      email: email.trim() || null,
      notify_after_seconds: notifyAfterMin * 60,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Add {scope} contact</h2>
          <button type="button" onClick={onClose} className="text-ink-mute hover:text-ink-dim">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Role (optional)</span>
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Site coordinator, on-call manager…"
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Phone</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+61…"
                className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">
              Escalation tier (notify after, minutes)
            </span>
            <input
              type="number"
              min={0}
              value={notifyAfterMin}
              onChange={(e) => setNotifyAfterMin(Math.max(0, Number(e.target.value) || 0))}
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
            />
            <span className="text-[11px] text-ink-faint">
              0 = notified immediately. Higher = only notified if alarm still unacknowledged after
              this many minutes.
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-ink-faint hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              Create contact
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
